import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import apis from "../../../context/api";

// eslint-disable-next-line react/prop-types
function QuotationEdit({ id, onSuccess, onCancel }) {
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [grandTotal, setGrandTotal] = useState(0);
  const [showCustomerFields, setShowCustomerFields] = useState(false);
  const [newCustomerFields, setNewCustomerFields] = useState({
    name: "",
    email: "",
    mobile: "",
    storeName: "",
    address: "",
  });
  const [comboData, setComboData] = useState({
    name: "",
    shortName: "",
    items: [],
    customerId: null,
    discount: "",
    userId: null,
  });
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    apis
      .getProducts()
      .then((data) => setProducts(data))
      .catch(() => setError("Failed to load products"));

    apis
      .getCustomers()
      .then((data) => setCustomers(data))
      .catch(() => setError("Failed to load customers"));
    if (id) {
      apis
        .getQuotationById(id)
        .then((data) => {
          setComboData(data);
          setCustomer(data.customer);
          calculateTotal(data.items);
        })
        .catch(() => setError("Failed to load quotation"));
    } else {
      setNewCustomerFields({
        name: "",
        email: "",
        mobile: "",
        storeName: "",
        address: "",
      });
      setComboData({
        name: "",
        shortName: "",
        items: [],
        customerId: null,
        discount: "",
        userId: null,
      });
    }
  }, []);

  const handleChange = (e) => {
    setComboData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleProductSelect = (event, values) => {
    const updatedItems = values.map((product) => {
      const existing = comboData.items.find((p) => p.productId === product.id);
      return {
        productId: product.id,
        quantity: existing?.quantity || 1,
        sellingPrice: product.sellingPrice || 0,
      };
    });
    setComboData((prev) => ({ ...prev, items: updatedItems }));
    calculateTotal(updatedItems);
  };

  const handleQuantityChange = (productId, quantity) => {
    if (quantity <= 0) {
      return;
    }
    const updated = comboData.items.map((item) =>
      item.productId === productId ? { ...item, quantity } : item
    );
    setComboData((prev) => ({ ...prev, items: updated }));
    calculateTotal(updated);
  };
  const handleSellingPriceChange = (productId, sellingPrice) => {
    const updated = comboData.items.map((item) =>
      item.productId === productId ? { ...item, sellingPrice } : item
    );
    setComboData((prev) => ({ ...prev, items: updated }));
    calculateTotal(updated);
  };

  const removeProduct = (productId) => {
    const filtered = comboData.items.filter((item) => item.productId !== productId);
    setComboData((prev) => ({ ...prev, items: filtered }));
  };

  const handleCustomerChange = (event, value) => {
    setCustomer(value);
    setShowCustomerFields(false);
    if (!value) {
      setNewCustomerFields({ name: "", email: "", mobile: "", storeName: "", address: "" });
    }
  };
  const calculateTotal = (updatedItems) => {
    let total = 0;
    updatedItems.forEach((item) => {
      const product = products.find((p) => p.id === item.productId);
      const price = item.sellingPrice ?? product?.sellingPrice ?? 0;
      const quantity = item.quantity ?? 1;
      total += price * quantity;
    });
    setGrandTotal(total.toFixed(2));
  };

  const handleNewCustomerChange = (e) => {
    const { name, value } = e.target;
    setNewCustomerFields((prev) => ({ ...prev, [name]: value }));
  };
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };
  const validateCustomerFields = () => {
    const newErrors = {};
    if (!newCustomerFields.mobile.trim()) newErrors.mobile = "Mobile  is required";
    if (!newCustomerFields.name.trim()) newErrors.name = "Name is required";
    if (!newCustomerFields.email.trim()) newErrors.email = "Email is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // returns true if no errors
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    let customerId = customer?.id;

    if (!customerId) {
      if (!validateCustomerFields()) return;
      // Create new customer first
      const newCust = await apis.createCustomer(newCustomerFields);
      customerId = newCust.id;
    }
    if (comboData.items.length == 0) {
      alert("please select product before proceeding");
      return;
    }

    const payload = { ...comboData, customerId, grandTotal: grandTotal, totalAmount: grandTotal };
    const method = id ? "PUT" : "POST";
    const url = id ? `quotations/${id}` : `quotations`;

    try {
      const response = await apis.quotationCreateUpdate(payload, method, url);
      if (!response.quotationId) throw new Error("Failed to save quotation");

      if (onSuccess) onSuccess(response);
    } catch (err) {
      setError(err.message);
    }
  };
  const newCustomer = () => {
    setCustomer(null);
    setShowCustomerFields(true);
    setNewCustomerFields({
      name: "",
      email: "",
      mobile: "",
      storeName: "",
      address: "",
      gstn: "",
    });
  };
  const selectedProducts = comboData.items
    .map((p) => products.find((prod) => prod.id === p.productId))
    .filter(Boolean);

  return (
    <MDBox pt={6} pb={6}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card>
            <MDBox p={5}>
              <Grid container spacing={2}>
                <Grid item xs={8} md={10}>
                  <Autocomplete
                    options={customers}
                    getOptionLabel={(option) => option.name || ""}
                    value={customer}
                    onChange={handleCustomerChange}
                    renderInput={(params) => <TextField {...params} label="Select Customer" />}
                    fullWidth
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />
                </Grid>
                <Grid item xs={2}>
                  <MDButton variant="gradient" color="info" onClick={newCustomer}>
                    New Customer
                  </MDButton>
                </Grid>

                {showCustomerFields && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Name"
                        name="name"
                        value={newCustomerFields.name}
                        onChange={handleNewCustomerChange}
                        fullWidth
                        error={!!errors.name}
                        helperText={errors.name}
                        FormHelperTextProps={{
                          sx: { color: "red" },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Email"
                        name="email"
                        type="email"
                        value={newCustomerFields.email}
                        onChange={handleNewCustomerChange}
                        fullWidth
                        error={!!errors.email}
                        helperText={errors.email}
                        FormHelperTextProps={{
                          sx: { color: "red" },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Mobile"
                        name="mobile"
                        type="number"
                        value={newCustomerFields.mobile}
                        onChange={handleNewCustomerChange}
                        fullWidth
                        error={!!errors.mobile}
                        helperText={errors.mobile}
                        FormHelperTextProps={{
                          sx: { color: "red" },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Store Name"
                        name="storeName"
                        value={newCustomerFields.storeName}
                        onChange={handleNewCustomerChange}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="GST No"
                        name="gstn"
                        value={newCustomerFields.gstn}
                        onChange={handleNewCustomerChange}
                        fullWidth
                        rows={2}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Address"
                        name="address"
                        value={newCustomerFields.address}
                        onChange={handleNewCustomerChange}
                        fullWidth
                        multiline
                        rows={2}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={products}
                    getOptionLabel={(option) => option.productShortName || ""}
                    value={selectedProducts}
                    onChange={handleProductSelect}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Products by Short Name" />
                    )}
                    fullWidth
                  />
                </Grid>

                {comboData.items.map((item) => {
                  const product = products.find((p) => p.id === item.productId);
                  return (
                    <Grid
                      container
                      spacing={1}
                      p={2}
                      key={item.productId}
                      alignItems="center"
                      mt={1}
                    >
                      <Grid item xs={5}>
                        <MDInput
                          label={product?.productShortName + " quantity" || "Product" + " quantity"}
                          value={item.quantity}
                          onChange={(e) => {
                            const value = Math.max(1, parseInt(e.target.value) || 1);
                            handleQuantityChange(item.productId, parseInt(value));
                          }}
                          type="number"
                          inputProps={{ min: 1 }}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <IconButton onClick={() => removeProduct(item.productId)}>
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                      <Grid item xs={5}>
                        <MDInput
                          label={product?.productShortName + " price" || "Product" + " price"}
                          value={item.sellingPrice}
                          onChange={(e) =>
                            handleSellingPriceChange(item.productId, parseInt(e.target.value))
                          }
                          type="number"
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  );
                })}
                {/*<Grid item xs={12} sm={6}>*/}
                {/*  <MDInput*/}
                {/*    label="Discount"*/}
                {/*    name="discount"*/}
                {/*    value={comboData.discount}*/}
                {/*    onChange={handleChange}*/}
                {/*    type="number"*/}
                {/*    fullWidth*/}
                {/*  />*/}
                {/*</Grid>*/}
              </Grid>
              <Grid item xs={12}>
                <MDBox mt={2} fontWeight="bold">
                  Total Amount: ₹ {grandTotal}
                </MDBox>
              </Grid>

              <Grid
                container
                justifyContent="flex-end"
                pt={4}
                spacing={1}
                display="flex"
                flexWrap="nowrap"
              >
                <Grid item pl={2} pr={2} xs="auto">
                  <MDButton variant="gradient" color="warning" onClick={handleCancel}>
                    Cancel
                  </MDButton>
                </Grid>
                <Grid item xs="auto">
                  <MDButton variant="gradient" color="info" onClick={handleSubmit}>
                    {id ? "Update Quotation" : "Create Quotation"}
                  </MDButton>
                </Grid>
              </Grid>

              {error && (
                <MDBox mt={2} color="error">
                  <strong>{error}</strong>
                </MDBox>
              )}
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}

export default QuotationEdit;
